import React, { useCallback } from "react";
import { Row } from "antd";
import { User } from "/app/src/models";
import Status from "../users/status";
import { useTranslation } from "react-i18next";
import { Button, useDisclosure, Modal } from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { userService } from "/app/src/services";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  useAuthDispatch,
  useAuthState,
  logout,
} from "/app/src/contexts/authentication";

/**
 * Renders the user menu component.
 *
 * @param {Object} props - The properties object.
 * @param {User} props.user - The user object representing the user.
 * @param {Function} props.toggleUserStatus - Function to toggle the user's status.
 * @returns {JSX.Element} The user menu component.
 */
export default function UserMenu({
  user,
  toggleUserStatus,
}: {
  user: User;
  toggleUserStatus: () => void;
}) {
  const navigate = useNavigate();
  const { user: loggedInUser, isAuthenticated } = useAuthState();
  const { t } = useTranslation();
  const dispatch = useAuthDispatch();
  const {
    isOpen: isDisableModalOpen,
    onOpen: openDisableModal,
    onOpenChange: openChangeDisableModal,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: openDeleteModal,
    onOpenChange: openChangeDeleteModal,
  } = useDisclosure();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuthState();

  const toggleUser = useCallback(() => {
    toggleUserStatus();
    openChangeDisableModal();
  }, [openChangeDisableModal, toggleUserStatus]);

  const { mutateAsync: deleteUser } = useMutation({
    mutationFn: (id: number) => userService.deleteSingle(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
    },
  });

  const handleLogout = useCallback(() => {
    logout(dispatch);
    navigate("/login");
  }, [dispatch, navigate]);

  const deleteUserHandler = useCallback(() => {
    deleteUser(user.id).then(() => {
      if (authUser.id === user.id) {
        handleLogout();
      } else {
        navigate("/users");
      }
    });
  }, [deleteUser, user.id, authUser.id, navigate, handleLogout]);

  const sendPasswordResetEmail = useCallback(() => {
    userService.reset({ search: user.email || user.username }).finally(() => {
      toast.success(t("translation:password_reset_email_sent"));
    });
  }, [user.email, user.username, t]);

  return (
    <div className="pl-[10px]">
      <Row>
        <h4>
          {t("translation:user")} {t("translation:status")}:
        </h4>
      </Row>
      <Row>
        <h2>
          <p>{Status(user.status)}</p>
        </h2>
      </Row>
      <Row>
        {isAuthenticated && loggedInUser?.id === user.id && (
          <Button
            fullWidth
            radius="none"
            color="default"
            variant="bordered"
            size="sm"
            onPress={handleLogout}
            className="hover:border-blue-500 hover:text-blue-500 bg-white"
          >
            {t("translation:log_out")}
          </Button>
        )}
        {loggedInUser?.isAdmin === true && (
          <Button
            fullWidth
            radius="none"
            color="default"
            variant="bordered"
            size="sm"
            onPress={sendPasswordResetEmail}
            className="hover:border-blue-500 hover:text-blue-500 bg-white"
          >
            {t("translation:send_reset_email")}
          </Button>
        )}
        {user.status !== 2 ? (
          <>
            <Button
              fullWidth
              radius="none"
              color="default"
              variant="bordered"
              size="sm"
              onPress={openDisableModal}
              className="border-[#a12121] text-[#a12121] bg-white"
            >
              {t("translation:deactivate")} {t("translation:user")}
            </Button>
            <Modal
              disableAnimation={false}
              isOpen={isDisableModalOpen}
              onOpenChange={openChangeDisableModal}
              placement="top"
              aria-label="Confirm deactivate user"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_deactivate_user"),
                modalFooter: (
                  <>
                    <Button
                      size="sm"
                      radius="none"
                      color="default"
                      variant="bordered"
                      onPress={openChangeDisableModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </Button>
                    <RedButton radius="none" size="sm" onPress={toggleUser}>
                      {t("translation:deactivate")}
                    </RedButton>
                  </>
                ),
              })}
            </Modal>
          </>
        ) : (
          <>
            <Button
              fullWidth
              color="primary"
              radius="none"
              variant="bordered"
              className="bg-white"
              size="sm"
              onPress={openDisableModal}
            >
              {t("translation:activate")} {t("translation:user")}
            </Button>
            <Modal
              disableAnimation={false}
              isOpen={isDisableModalOpen}
              onOpenChange={openChangeDisableModal}
              placement="top"
              aria-label="Confirm activate user"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_activate_user"),
                modalFooter: (
                  <>
                    <Button
                      size="sm"
                      radius="none"
                      color="default"
                      variant="bordered"
                      onPress={openChangeDisableModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </Button>
                    <Button
                      size="sm"
                      radius="none"
                      color="primary"
                      onPress={toggleUser}
                    >
                      {t("translation:activate")}
                    </Button>
                  </>
                ),
              })}
            </Modal>
          </>
        )}
        {user?.status === 2 && (
          <>
            <Button
              fullWidth
              radius="none"
              color="default"
              variant="bordered"
              size="sm"
              onPress={openDeleteModal}
              className="border-[#a12121] text-[#a12121] bg-white"
            >
              {t("translation:delete_user")}
            </Button>
            <Modal
              disableAnimation={false}
              isOpen={isDeleteModalOpen}
              onOpenChange={openChangeDeleteModal}
              placement="top"
              aria-label="Confirm delete user"
            >
              {ModalBuilder({
                warning: true,
                modalHeader: t("translation:warning"),
                modalBody: t("translation:confirm_delete_user"),
                modalFooter: (
                  <>
                    <Button
                      size="sm"
                      radius="none"
                      color="default"
                      variant="bordered"
                      onPress={openChangeDeleteModal}
                      className="hover:border-primary-default hover:text-primary-default"
                    >
                      {t("translation:cancel")}
                    </Button>
                    <RedButton
                      size="sm"
                      radius="none"
                      onPress={deleteUserHandler}
                    >
                      {t("translation:delete")}
                    </RedButton>
                  </>
                ),
              })}
            </Modal>
          </>
        )}
      </Row>
    </div>
  );
}
